import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ACTIVE_CLASS = 'is-active';
const DONE_CLASS = 'is-done';
const REDUCED_CLASS = 'is-reduced-motion';
const DESKTOP_QUERY = '(min-width: 1181px) and (prefers-reduced-motion: no-preference)';
const STAGE_QUERY = '(max-width: 1180px) and (prefers-reduced-motion: no-preference)';

function prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function setText(root: ParentNode, selector: string, value: string | undefined): void {
    if (value === undefined) return;

    root.querySelectorAll<HTMLElement>(selector).forEach(element => {
        if (element.textContent !== value) {
            element.textContent = value;
        }
    });
}

function splitTokens(value: string | undefined): string[] {
    return (value ?? '')
        .split(',')
        .map(token => token.trim())
        .filter(Boolean);
}

function clampIndex(index: number, count: number): number {
    return Math.max(0, Math.min(index, count - 1));
}

function createPinnedStory(
    root: HTMLElement,
    count: number,
    activate: (index: number) => void,
    options: { id: string; distance?: number; pin?: HTMLElement | null }):
    () => void {
    if (count <= 0) return () => undefined;

    const pinTarget = options.pin ?? root;
    root.classList.add('is-pinned-story');
    root.style.setProperty('--story-progress', '0');
    let current = -1;
    const snapToStep = (progress: number): number => {
        if (count <= 1) return 0;
        return clampIndex(Math.round(progress * (count - 1)), count) / (count - 1);
    };

    const trigger = ScrollTrigger.create({
        id: options.id,
        trigger: pinTarget,
        start: 'top top',
        end: () => '+=' + Math.round(window.innerHeight * (options.distance ?? Math.max(count - 1, 1))),
        scrub: 0.42,
        pin: pinTarget,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        snap: count > 1
            ? {
                snapTo: snapToStep,
                inertia: false,
                duration: { min: 0.18, max: 0.44 },
                delay: 0.01,
                ease: 'power2.out'
            }
            : false,
        onUpdate: self => {
            const index = count === 1 ? 0 : clampIndex(Math.round(self.progress * (count - 1)), count);
            root.style.setProperty('--story-progress', self.progress.toFixed(4));
            if (index === current) return;
            current = index;
            activate(index);
        },
        onRefresh: self => {
            const index = count === 1 ? 0 : clampIndex(Math.round(self.progress * (count - 1)), count);
            root.style.setProperty('--story-progress', self.progress.toFixed(4));
            current = index;
            activate(index);
        }
    });

    activate(0);

    return () => {
        trigger.kill();
        root.classList.remove('is-pinned-story');
        root.style.removeProperty('--story-progress');
    };
}

function setDoneStates(steps: HTMLElement[], activeIndex: number): void {
    steps.forEach((candidate, candidateIndex) => {
        candidate.classList.toggle(ACTIVE_CLASS, candidateIndex === activeIndex);
        candidate.classList.toggle(DONE_CLASS, candidateIndex < activeIndex);
    });
}

function initWhatWeDoStory(): void {
    const root = document.querySelector<HTMLElement>('[data-what-story]');
    if (!root) return;

    const steps = Array.from(root.querySelectorAll<HTMLElement>('[data-what-step]'));
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-what-layer].sys-node'));
    const paths = Array.from(root.querySelectorAll<SVGPathElement>('.offer-mesh__path[data-what-layer]'));
    const layerOrder = ['foundation', 'services', 'outcome'];

    if (!steps.length) return;

    const activate = (index: number): void => {
        const safeIndex = clampIndex(index, steps.length);
        const step = steps[safeIndex];
        const layers = splitTokens(step.dataset.layers);
        const layerSet = new Set(layers);
        const activeLayerIndex = layerOrder.findIndex(layer => layerSet.has(layer));

        root.dataset.activeStep = safeIndex.toString();
        setDoneStates(steps, safeIndex);

        nodes.forEach(node => {
            const layer = node.dataset.whatLayer ?? '';
            const layerIndex = layerOrder.indexOf(layer);
            node.classList.toggle(ACTIVE_CLASS, layerSet.has(layer));
            node.classList.toggle(DONE_CLASS, layerIndex >= 0 && layerIndex < activeLayerIndex);
        });

        paths.forEach(path => {
            const layer = path.dataset.whatLayer ?? '';
            const layerIndex = layerOrder.indexOf(layer);
            path.classList.toggle(ACTIVE_CLASS, layerSet.has(layer));
            path.classList.toggle(DONE_CLASS, layerIndex >= 0 && layerIndex < activeLayerIndex);
        });
    };

    activate(0);

    if (prefersReducedMotion()) {
        root.classList.add(REDUCED_CLASS);
        return;
    }

    const media = gsap.matchMedia();
    media.add(DESKTOP_QUERY, () => createPinnedStory(root, steps.length, activate, { id: 'landing-what-story', distance: 2.25 }));
    media.add(STAGE_QUERY, () => createPinnedStory(
        root,
        steps.length,
        activate,
        {
            id: 'landing-what-story-stage',
            distance: 2.1,
            pin: root.querySelector<HTMLElement>('.offer__layout')
        }));
}

function initProductStory(): void {
    const root = document.querySelector<HTMLElement>('[data-product-story]');
    if (!root) return;

    const steps = Array.from(root.querySelectorAll<HTMLElement>('[data-product-step]'));
    const progress = root.querySelector<HTMLElement>('[data-product-progress]');
    if (!steps.length) return;

    const activate = (index: number): void => {
        const safeIndex = clampIndex(index, steps.length);
        const step = steps[safeIndex];

        const tone = step.dataset.tone ?? 'discovery';
        root.dataset.productTone = tone;
        root.dataset.activeStep = safeIndex.toString();
        setDoneStates(steps, safeIndex);
    };

    activate(0);

    if (prefersReducedMotion()) {
        root.classList.add(REDUCED_CLASS);
        progress?.style.setProperty('transform', 'scaleY(1)');
        return;
    }

    const media = gsap.matchMedia();
    media.add(DESKTOP_QUERY, () => createPinnedStory(root, steps.length, activate, { id: 'landing-product-story', distance: 4.25 }));
    media.add(STAGE_QUERY, () => createPinnedStory(
        root,
        steps.length,
        activate,
        {
            id: 'landing-product-story-stage',
            distance: 4.25,
            pin: root.querySelector<HTMLElement>('.product-story__layout')
        }));
}

function initPhilosophyStory(): void {
    const root = document.querySelector<HTMLElement>('[data-philosophy-story]');
    if (!root) return;

    const steps = Array.from(root.querySelectorAll<HTMLElement>('[data-philosophy-step]'));
    const focusItems = Array.from(root.querySelectorAll<HTMLElement>('[data-iki-focus]'));
    if (!steps.length) return;

    const activate = (index: number): void => {
        const safeIndex = clampIndex(index, steps.length);
        const step = steps[safeIndex];
        const concept = step.dataset.concept ?? 'kaizen';
        const focus = step.dataset.focus ?? 'center';

        root.dataset.activeConcept = concept;
        root.dataset.activeStep = safeIndex.toString();
        setDoneStates(steps, safeIndex);
        focusItems.forEach(item => item.classList.toggle(ACTIVE_CLASS, item.dataset.ikiFocus === focus));

        setText(root, '[data-philosophy-symbol]', step.dataset.symbol);
        setText(root, '[data-philosophy-panel-kicker]', step.dataset.title);
        setText(root, '[data-philosophy-panel-title]', step.dataset.title);
        setText(root, '[data-philosophy-panel-body]', step.dataset.body);
    };

    activate(0);

    if (prefersReducedMotion()) {
        root.classList.add(REDUCED_CLASS);
        return;
    }

    const media = gsap.matchMedia();
    media.add(DESKTOP_QUERY, () => createPinnedStory(root, steps.length, activate, { id: 'landing-philosophy-story', distance: 4.25 }));
    media.add(STAGE_QUERY, () => createPinnedStory(
        root,
        steps.length,
        activate,
        {
            id: 'landing-philosophy-story-stage',
            distance: 4.25,
            pin: root.querySelector<HTMLElement>('.philosophy-story__layout')
        }));
}

function initEngagementStory(): void {
    const root = document.querySelector<HTMLElement>('[data-engagement-story]');
    if (!root) return;

    const steps = Array.from(root.querySelectorAll<HTMLElement>('[data-engagement-step]'));
    const markers = Array.from(root.querySelectorAll<HTMLElement>('[data-engagement-marker]'));
    const progress = root.querySelector<HTMLElement>('[data-engagement-progress]');
    if (!steps.length) return;

    const activate = (index: number): void => {
        const safeIndex = clampIndex(index, steps.length);

        root.dataset.activeStep = safeIndex.toString();
        setDoneStates(steps, safeIndex);
        markers.forEach((marker, markerIndex) => {
            marker.classList.toggle(ACTIVE_CLASS, markerIndex === safeIndex);
            marker.classList.toggle(DONE_CLASS, markerIndex < safeIndex);
        });

        progress?.style.setProperty('--stage', safeIndex.toString());
    };

    activate(0);

    if (prefersReducedMotion()) {
        root.classList.add(REDUCED_CLASS);
        progress?.style.setProperty('--p', '1');
        return;
    }

    const media = gsap.matchMedia();
    media.add(DESKTOP_QUERY, () => createPinnedStory(root, steps.length, activate, { id: 'landing-engagement-story', distance: 4.25 }));
    media.add(STAGE_QUERY, () => createPinnedStory(
        root,
        steps.length,
        activate,
        {
            id: 'landing-engagement-story-stage',
            distance: 4.25,
            pin: root.querySelector<HTMLElement>('.engagement-story__layout')
        }));
}

function initSectionHeadAnimations(): void {
    if (prefersReducedMotion()) return;

    const heads = Array.from(document.querySelectorAll<HTMLElement>(
        '.section__head, .product-story__intro, .philosophy-story__intro'
    ));

    heads.forEach(head => {
        const children = Array.from(head.children);
        gsap.fromTo(children,
            { autoAlpha: 0, y: 28 },
            {
                autoAlpha: 1,
                y: 0,
                duration: 0.72,
                ease: 'power3.out',
                stagger: 0.08,
                scrollTrigger: {
                    trigger: head,
                    start: 'top 82%',
                    once: true
                }
            }
        );
    });
}

function initLandingFlowTransitions(): void {
    if (prefersReducedMotion()) return;

    const storySelectors = [
        '[data-what-story]',
        '[data-product-story]',
        '[data-value-offer]',
        '[data-philosophy-story]',
        '[data-engagement-story]'
    ];
    const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-flow-section]'));
    sections.forEach(section => {
        const content = section.querySelector<HTMLElement>(':scope > .page-shell') ?? section;
        section.classList.add('landing-flow-section');

        if (storySelectors.some(selector => section.matches(selector))) return;

        gsap.fromTo(content,
            { autoAlpha: 0.72, y: 54, scale: 0.99 },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: section,
                    start: 'top 78%',
                    once: true
                }
            }
        );
    });
}

function initValueOfferSystem(): void {
    const root = document.querySelector<HTMLElement>('[data-value-offer]');
    if (!root) return;

    const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-offer-card]'));
    const progress = root.querySelector<HTMLElement>('[data-value-progress]');
    const casePanel = root.querySelector<HTMLElement>('.offering-story__case');
    const visual = root.querySelector<HTMLElement>('[data-value-visual]');
    const link = root.querySelector<HTMLAnchorElement>('[data-value-stage-link]');
    const primaryIcon = root.querySelector<HTMLImageElement>('[data-value-icon-primary]');
    const secondaryIcon = root.querySelector<HTMLImageElement>('[data-value-icon-secondary]');
    const tertiaryIcon = root.querySelector<HTMLImageElement>('[data-value-icon-tertiary]');
    if (!cards.length) return;

    const activate = (index: number): void => {
        const safeIndex = clampIndex(index, cards.length);
        const card = cards[safeIndex];
        const tone = card.dataset.stageTone ?? 'build';

        root.dataset.activeOffer = safeIndex.toString();
        root.dataset.offerTone = tone;
        cards.forEach((candidate, candidateIndex) => {
            candidate.classList.toggle(ACTIVE_CLASS, candidateIndex === safeIndex);
            candidate.classList.toggle(DONE_CLASS, candidateIndex < safeIndex);
        });

        setText(root, '[data-value-case-label]', card.dataset.stageLabel);
        setText(root, '[data-value-stage-problem]', card.dataset.stageProblem);
        setText(root, '[data-value-stage-body]', card.dataset.stageBody);
        setText(root, '[data-value-stage-outcome]', card.dataset.stageOutcome);

        if (link) {
            link.href = card.dataset.stageHref ?? card.getAttribute('href') ?? link.href;
        }

        if (primaryIcon && card.dataset.stageIconPrimary) {
            primaryIcon.src = card.dataset.stageIconPrimary;
        }

        if (secondaryIcon && card.dataset.stageIconSecondary) {
            secondaryIcon.src = card.dataset.stageIconSecondary;
        }

        if (tertiaryIcon && card.dataset.stageIconTertiary) {
            tertiaryIcon.src = card.dataset.stageIconTertiary;
        }

        progress?.style.setProperty('--stage', safeIndex.toString());

        if (!prefersReducedMotion() && window.matchMedia('(min-width: 1181px)').matches) {
            const direction = safeIndex === 0 ? -1 : safeIndex === cards.length - 1 ? 1 : 0;
            if (casePanel) {
                gsap.fromTo(casePanel,
                    { autoAlpha: 0.84, x: 24 * (direction || 1), filter: 'blur(5px)' },
                    { autoAlpha: 1, x: 0, filter: 'blur(0px)', duration: 0.46, ease: 'power3.out', overwrite: 'auto' }
                );
            }

            if (visual) {
                gsap.fromTo(visual,
                    { autoAlpha: 0.9, scale: 0.985 },
                    { autoAlpha: 1, scale: 1, duration: 0.5, ease: 'power3.out', overwrite: 'auto' }
                );
            }
        } else {
            const stableElements = [casePanel, visual].filter(Boolean) as HTMLElement[];
            gsap.killTweensOf(stableElements);
            gsap.set(stableElements, { autoAlpha: 1, x: 0, scale: 1, filter: 'blur(0px)' });
        }
    };

    activate(0);

    if (prefersReducedMotion()) {
        root.classList.add(REDUCED_CLASS);
        progress?.style.setProperty('--p', '1');
        return;
    }

    const media = gsap.matchMedia();
    media.add(DESKTOP_QUERY, () => createPinnedStory(root, cards.length, activate, { id: 'landing-value-offer', distance: 2.6 }));
    media.add(STAGE_QUERY, () => createPinnedStory(
        root,
        cards.length,
        activate,
        {
            id: 'landing-value-offer-stage',
            distance: 2.4,
            pin: root.querySelector<HTMLElement>('.offering-story__stage')
        }));
}

function initStaggeredCards(): void {
    if (prefersReducedMotion()) return;

    const cardGroups: Array<{ trigger: string; selector: string; y: number; scale: number }> = [
        { trigger: '[data-craft-proof]', selector: '[data-craft-card]', y: 54, scale: 0.965 },
        { trigger: '#blog', selector: '#blog .blog-preview-grid > *', y: 46, scale: 0.97 }
    ];

    cardGroups.forEach(group => {
        const cards = Array.from(document.querySelectorAll<HTMLElement>(group.selector));
        if (!cards.length) return;

        gsap.fromTo(cards,
            { autoAlpha: 0, y: group.y, scale: group.scale },
            {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 0.82,
                ease: 'power3.out',
                stagger: 0.075,
                scrollTrigger: {
                    trigger: group.trigger,
                    start: 'top 72%',
                    once: true
                }
            }
        );
    });
}

export function initLandingScrollStories(): void {
    initLandingFlowTransitions();
    initWhatWeDoStory();
    initProductStory();
    initValueOfferSystem();
    initPhilosophyStory();
    initEngagementStory();
    initSectionHeadAnimations();
    initStaggeredCards();

    window.addEventListener('load', () => {
        ScrollTrigger.refresh();

        if (!window.location.hash) return;
        const target = document.querySelector<HTMLElement>(window.location.hash);
        window.setTimeout(() => target?.scrollIntoView({ block: 'start' }), 80);
    }, { once: true });
}
